/**
 * PRP-MCP-13 work item 5: on the estimate page (V2SupplementPage, /completeness), an
 * estimate started from a field project (start_estimate_from_project writes
 * `estimate.metadata.field_project = {project_id, name}`) shows "From field project
 * <name>" with a link to the project page, and the project's read-only floor plan
 * (FloorPlanView, drawn from the project's notes) when a room has geometry.
 * Nothing renders for any other estimate. No new page.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BackendAPI } from '../../lib/api';
import type { FieldNote } from '../../types';
import { FloorPlanView } from './FloorPlanView';

export interface FieldProjectLink { project_id: string; name?: string | null }

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The field project an estimate was started from, or null. Only a uuid project id counts: the id
 *  goes into an API path, so anything else (e.g. "../x") is refused (MCP-13 security review L5). */
export function fieldProjectOf(estimate: unknown): FieldProjectLink | null {
  const meta = (estimate as { metadata?: { field_project?: unknown } } | null | undefined)?.metadata;
  const fp = meta?.field_project as FieldProjectLink | undefined;
  return fp && typeof fp.project_id === 'string' && UUID.test(fp.project_id) ? fp : null;
}

export function FieldProjectBanner({ estimate }: { estimate: unknown }) {
  const fp = fieldProjectOf(estimate);
  const projectId = fp?.project_id ?? null;
  const [notes, setNotes] = useState<FieldNote[]>([]);
  useEffect(() => {
    setNotes([]);
    if (!projectId) return;
    let cancelled = false;
    BackendAPI.getNotesForProject(projectId)
      .then((n) => {
        if (!cancelled) setNotes(Array.isArray(n) ? n : []);
      })
      .catch(() => {
        if (!cancelled) setNotes([]);   // the banner still shows; the plan is extra
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  if (!fp) return null;
  return (
    <section data-testid="field-project-banner" className="mb-4">
      <div className="rounded-lg border border-[var(--bd)] bg-[var(--bg-1)] px-4 py-3 text-[13px] text-[var(--tx-lo)]">
        From field project{' '}
        <Link to={`/project/${encodeURIComponent(fp.project_id)}`} className="font-[560] text-[var(--tx-hi)] underline">
          {fp.name || 'project'}
        </Link>
      </div>
      <FloorPlanView notes={notes} className="mt-3" />
    </section>
  );
}

export default FieldProjectBanner;
